export type QueryHandler =
  | 'findDirectReports'
  | 'findManager'
  | 'filterToTeam'
  | 'filterToDepartment'
  | 'countInDepartment'
  | 'findByName'
  | 'findByTitle'
  | 'showReportingChain'
  | 'findLongestTenure'
  | 'findBySkill';

export interface StructuredQuery {
  type: QueryHandler;
  params: string[];
  originalQuestion: string;
  confidence: number;
}

interface QueryPattern {
  regex: RegExp;
  handler: QueryHandler;
  confidence: number;
}

export class StructuredQA {
  private patterns: QueryPattern[] = [
    {
      regex: /who reports to (.+)/i,
      handler: 'findDirectReports',
      confidence: 0.95,
    },
    {
      regex: /who does (.+) report to/i,
      handler: 'findManager',
      confidence: 0.95,
    },
    {
      regex: /show me (.+) team/i,
      handler: 'filterToTeam',
      confidence: 0.9,
    },
    {
      regex: /show (.+) department/i,
      handler: 'filterToDepartment',
      confidence: 0.9,
    },
    {
      regex: /how many people in (.+)/i,
      handler: 'countInDepartment',
      confidence: 0.85,
    },
    {
      regex: /find (.+)/i,
      handler: 'findByName',
      confidence: 0.7,
    },
    {
      regex: /where is (.+)/i,
      handler: 'findByName',
      confidence: 0.8,
    },
    {
      regex: /show reporting chain to (.+)/i,
      handler: 'showReportingChain',
      confidence: 0.9,
    },
    {
      regex: /reporting chain for (.+)/i,
      handler: 'showReportingChain',
      confidence: 0.9,
    },
    {
      regex: /who has been here longest/i,
      handler: 'findLongestTenure',
      confidence: 0.95,
    },
    {
      regex: /longest tenure/i,
      handler: 'findLongestTenure',
      confidence: 0.9,
    },
    {
      regex: /find people with (.+)/i,
      handler: 'findBySkill',
      confidence: 0.8,
    },
    {
      regex: /who has (.+) skill/i,
      handler: 'findBySkill',
      confidence: 0.85,
    },
    {
      regex: /people with (.+) title/i,
      handler: 'findByTitle',
      confidence: 0.85,
    },
    {
      regex: /all (.+)s\b/i,
      handler: 'findByTitle',
      confidence: 0.7,
    },
  ];

  tryParse(question: string): StructuredQuery | null {
    const trimmedQuestion = question.trim();

    for (const pattern of this.patterns) {
      const match = trimmedQuestion.match(pattern.regex);

      if (match) {
        const params = match.slice(1).map((p) => cleanSubject(p)).filter(Boolean);
        if (params.length < match.length - 1) continue;
        return {
          type: pattern.handler,
          params,
          originalQuestion: trimmedQuestion,
          confidence: pattern.confidence,
        };
      }
    }

    return null;
  }

  getSupportedPatterns(): Array<{ pattern: string; example: string; handler: QueryHandler }> {
    return [
      { pattern: 'who reports to [name]', example: 'Who reports to Sarah?', handler: 'findDirectReports' },
      { pattern: 'who does [name] report to', example: 'Who does John report to?', handler: 'findManager' },
      { pattern: 'show me [team] team', example: 'Show me Engineering team', handler: 'filterToTeam' },
      { pattern: 'show [department] department', example: 'Show Sales department', handler: 'filterToDepartment' },
      { pattern: 'how many people in [department]', example: 'How many people in Marketing?', handler: 'countInDepartment' },
      { pattern: 'find [name]', example: 'Find John Smith', handler: 'findByName' },
      { pattern: 'where is [name]', example: 'Where is Jane Doe?', handler: 'findByName' },
      { pattern: 'show reporting chain to [name]', example: 'Show reporting chain to CEO', handler: 'showReportingChain' },
      { pattern: 'who has been here longest', example: 'Who has been here longest?', handler: 'findLongestTenure' },
      { pattern: 'find people with [skill]', example: 'Find people with Python', handler: 'findBySkill' },
    ];
  }

  registerPattern(regex: RegExp, handler: QueryHandler, confidence: number = 0.8): void {
    this.patterns.unshift({ regex, handler, confidence });
  }
}

const TRAILING_SCOPE_CLAUSE =
  /\s+(?:on|in|within|from)\s+(?:the|this|my|our)?\s*(?:org(?:anisation|anization)?\s*chart|org|chart|diagram|canvas|tree|company|team|board)\b.*$/i;

/**
 * A captured subject is the text after the pattern's verb, so a natural
 * question drags the rest of the sentence into the name: "where is Howard
 * Clark on the chart? Select him." must look up "Howard Clark", not the whole
 * tail. Cut at the first sentence break, drop a trailing scope clause, and
 * strip quotes and filler so the lookup sees only the subject.
 */
function cleanSubject(raw: string | undefined): string {
  if (!raw) return '';
  // Sentence breaks end the subject; a bare period is kept so initials and
  // abbreviated titles ("J. Smith", "Sr. Engineer") survive.
  let subject = raw.split(/[?!;\n]/, 1)[0] ?? '';
  subject = subject.replace(TRAILING_SCOPE_CLAUSE, '');
  subject = subject.replace(/,.*$/, '');
  subject = subject.replace(/\s+please$/i, '');
  subject = subject.replace(/\.+$/, '');
  subject = subject.replace(/^["'“‘]+|["'”’]+$/g, '');
  return subject.trim();
}
